import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { organizationApi, Organization } from '../../services/api';
import { CodeDisplayModal } from '../../components/CodeDisplayModal';

export function OrganizationsList(): JSX.Element {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [codeModalTitle, setCodeModalTitle] = useState('');

  // Form state
  const [formName, setFormName] = useState('');
  const [formStoreId, setFormStoreId] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadOrganizations = async () => {
    try {
      setIsLoading(true);
      const response = await organizationApi.getAll();
      setOrganizations(response.organizations || []);
    } catch (err) {
      setError('Fehler beim Laden der Märkte');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    const nameToCreate = formName;

    try {
      const response = await organizationApi.create({
        name: formName,
        storeId: formStoreId.toUpperCase(),
      });
      setShowCreateForm(false);
      setFormName('');
      setFormStoreId('');

      // Show the generated code
      if (response.accessCode) {
        setCodeModalTitle(`Zugangscode für "${nameToCreate}"`);
        setGeneratedCode(response.accessCode);
      }

      loadOrganizations();
    } catch (err: any) {
      setFormError(err.message || 'Fehler beim Erstellen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrg) return;

    setFormError('');
    setIsSubmitting(true);

    try {
      await organizationApi.update(editingOrg.id, {
        name: formName,
        storeId: formStoreId.toUpperCase(),
      });
      setEditingOrg(null);
      setFormName('');
      setFormStoreId('');
      loadOrganizations();
    } catch (err: any) {
      setFormError(err.message || 'Fehler beim Aktualisieren');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (org: Organization) => {
    if (!window.confirm(`Markt "${org.name}" wirklich deaktivieren?`)) return;

    try {
      await organizationApi.delete(org.id);
      loadOrganizations();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Deaktivieren');
    }
  };

  const handleRegenerateCode = async (org: Organization) => {
    if (!window.confirm(`Zugangscode für "${org.name}" wirklich erneuern?\n\nDer alte Code wird sofort ungültig!`)) return;

    try {
      const response = await organizationApi.regenerateCode(org.id);
      if (response.accessCode) {
        setCodeModalTitle(`Neuer Zugangscode für "${org.name}"`);
        setGeneratedCode(response.accessCode);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Erneuern des Codes');
    }
  };

  const startEdit = (org: Organization) => {
    setEditingOrg(org);
    setFormName(org.name);
    setFormStoreId(org.storeId);
    setShowCreateForm(false);
  };

  const cancelEdit = () => {
    setEditingOrg(null);
    setShowCreateForm(false);
    setFormName('');
    setFormStoreId('');
    setFormError('');
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Märkte</h1>
        {user?.isSuperAdmin && !showCreateForm && !editingOrg && (
          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            Neuer Markt
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {(showCreateForm || editingOrg) && (
        <div className="form-card">
          <h2>{editingOrg ? 'Markt bearbeiten' : 'Neuer Markt'}</h2>
          <form onSubmit={editingOrg ? handleUpdate : handleCreate}>
            {formError && <div className="form-error">{formError}</div>}

            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="z.B. Hauptmarkt Berlin"
                required
                minLength={2}
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label htmlFor="storeId">Store-ID</label>
              <input
                type="text"
                id="storeId"
                value={formStoreId}
                onChange={e => setFormStoreId(e.target.value.toUpperCase())}
                placeholder="z.B. D001"
                required
                pattern="D[A-Z0-9]{3}"
                title="Format: DXXX (z.B. D001, DA12)"
              />
              <small>Format: DXXX (D gefolgt von 3 Zeichen)</small>
            </div>

            {!editingOrg && (
              <div className="form-info">
                <strong>Hinweis:</strong> Beim Erstellen wird automatisch ein Zugangscode generiert.
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Speichern...' : 'Speichern'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Store-ID</th>
              <th>Status</th>
              <th>Erstellt</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {organizations.length === 0 ? (
              <tr>
                <td colSpan={5} className="no-data">Keine Märkte vorhanden</td>
              </tr>
            ) : (
              organizations.map(org => (
                <tr key={org.id} className={!org.isActive ? 'inactive' : ''}>
                  <td>{org.name}</td>
                  <td><code>{org.storeId}</code></td>
                  <td>
                    <span className={`status-badge ${org.isActive ? 'active' : 'inactive'}`}>
                      {org.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>{new Date(org.createdAt).toLocaleDateString('de-DE')}</td>
                  <td className="actions">
                    <Link to={`/admin/organizations/${org.id}`} className="btn btn-small btn-primary">
                      Einstellungen
                    </Link>
                    <button className="btn btn-small" onClick={() => startEdit(org)}>
                      Bearbeiten
                    </button>
                    {org.isActive && (
                      <button
                        className="btn btn-small btn-warning"
                        onClick={() => handleRegenerateCode(org)}
                        title="Neuen Zugangscode generieren"
                      >
                        Code erneuern
                      </button>
                    )}
                    {user?.isSuperAdmin && org.isActive && (
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(org)}
                      >
                        Deaktivieren
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {generatedCode && (
        <CodeDisplayModal
          code={generatedCode}
          title={codeModalTitle}
          onClose={() => setGeneratedCode(null)}
        />
      )}
    </div>
  );
}

export default OrganizationsList;
